// Pure helpers for the TEC influence scorecard (plan 2154 phase 4). A lift is never rendered without its n,
// open share and interval; a flat or negative result is worded as such (reporting policy D8).
export const HORIZONS = [90, 180, 365];
export const DESIGNS = {
  pooled: {
    key: 'pooled',
    title: 'Win-rate lift, all TEC-influenced deals',
    short: 'All influenced deals',
    question: 'Across every deal with an attending SE (already open at the first attendance, or created after it), how much more (or less) often did they win within the horizon than comparable deals of the same age and size with no attending SE? An association, not proof of effect.',
  },
  landmark: {
    key: 'landmark',
    title: 'Deals already open at the first attendance',
    short: 'Open at first attendance',
    question: 'Of deals that were already open when a team member first attended, how much more (or less) often did they win within the horizon than comparable deals with no attending SE?',
  },
  created_after: {
    key: 'created_after',
    title: 'Deals created after the first attendance',
    short: 'Created after first attendance',
    question: 'Of deals created after a team member first attended, how much more (or less) often did they win within the horizon than comparable deals of the same age and size with no attending SE?',
  },
};

export const fmtPts = (v, digits = 1) => (v == null || Number.isNaN(v) ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(digits)} pts`);
export const fmtPct = (v, digits = 0) => (v == null || Number.isNaN(v) ? '—' : `${Number(v).toFixed(digits)}%`);
export const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString());
export const fmtUsd = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const s = a >= 1e9 ? `$${(a / 1e9).toFixed(2)}B` : a >= 1e6 ? `$${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `$${(a / 1e3).toFixed(0)}K` : `$${a.toFixed(0)}`;
  return v < 0 ? `−${s}` : s;
};
export const fmtInterval = (lo, hi, digits = 1) => (lo == null || hi == null ? '—' : `[${fmtPts(lo, digits).replace(' pts', '')}, ${fmtPts(hi, digits).replace(' pts', '')}]`);

// 'positive' | 'negative' | 'flat' | 'insufficient'
export function verdict(lift, lo, hi) {
  if (lift == null || lo == null || hi == null) return 'insufficient';
  if (lo > 0) return 'positive';
  if (hi < 0) return 'negative';
  return 'flat';
}

export const VERDICT_COLOR = { positive: '#2e7d32', negative: '#c62828', flat: '#616161', insufficient: '#9e9e9e' };

export function readHeadline(headline, design, horizon) {
  const h = headline?.[design]?.[String(horizon)];
  if (!h) return null;
  const adj = h.adjusted || {};
  const insufficient = !!adj.insufficient || adj.lift_pts == null;
  return {
    horizon,
    lift: insufficient ? null : adj.lift_pts,
    lo: insufficient ? null : adj.ci_lo_pts,
    hi: insufficient ? null : adj.ci_hi_pts,
    treatedRate: h.treated?.win_rate_pct,
    baselineStdRate: adj.baseline_standardised_rate_pct,
    nClosed: h.treated?.n_closed ?? 0,
    nDeals: h.treated?.n_deals ?? h.n,
    openSharePct: h.open_share_pct,
    baselineOpenSharePct: h.baseline_open_share_pct,
    baselineN: h.baseline?.n_closed,
    rawLift: h.raw?.lift_pts,
    composition: h.composition || null,
    verdict: verdict(insufficient ? null : adj.lift_pts, adj.ci_lo_pts, adj.ci_hi_pts),
  };
}

// One sentence an exec can repeat. Always carries n, open share and interval.
export function headlineSentence(r, design, { withTail = true } = {}) {
  if (!r) return '';
  const when = design === 'pooled' ? 'with an attending SE (open at first attendance or created after it)' : design === 'landmark' ? 'that were already open when an SE first attended' : 'created after an SE first attended';
  if (r.verdict === 'insufficient') {
    return `Not enough closed deals ${when} to report a reliable difference at ${r.horizon} days (${fmtInt(r.nClosed)} closed, ${fmtPct(r.openSharePct)} still open).`;
  }
  const iv = `95% interval ${fmtInterval(r.lo, r.hi)}`;
  const tail = withTail ? ` n = ${fmtInt(r.nClosed)} closed deals; ${fmtPct(r.openSharePct)} still open; ${iv}.` : '';
  if (r.verdict === 'flat') {
    return `For deals ${when}, win rate within ${r.horizon} days is ${fmtPts(r.lift)} versus comparable deals with no attending SE: no clear difference (the interval includes zero).${tail}`;
  }
  const dir = r.verdict === 'positive' ? 'higher' : 'lower';
  return `For deals ${when}, win rate within ${r.horizon} days is ${fmtPts(Math.abs(r.lift)).replace(/^[+−]/, '')} ${dir} than for comparable deals with no attending SE.${tail}`;
}

export const hasPooled = (headline) => !!headline?.pooled;

// "98% created after first attendance / 2% already open at first attendance"
export function compositionText(c) {
  if (!c || c.created_after_share_pct == null || c.landmark_share_pct == null) return '';
  return `${fmtPct(c.created_after_share_pct)} of these closed deals were created after the first attendance, ${fmtPct(c.landmark_share_pct)} were already open at the first attendance.`;
}

export function pathRows(volume) {
  const p = volume?.paths || {};
  const total = volume?.total_in_scope || {};
  const label = {
    direct: 'Direct (team member or contact role)',
    customer_account_confirmed: 'Customer account (confirmed match)',
    partner_confirmed: 'Partner (confirmed match)',
    pending_review: 'Pending review (not counted in any total)',
  };
  return Object.keys(label).filter((k) => p[k]).map((k) => ({
    key: k, label: label[k], unconfirmed: k === 'pending_review', ...p[k],
    sharePct: p[k].share_of_all_deals_pct, totalDeals: total.n_deals,
  }));
}

export function programOptions(tags) {
  return optionsFor(tags, 'TEC Program');
}
export function productOptions(tags) {
  return optionsFor(tags, 'Product');
}
function optionsFor(tags, category) {
  return Object.entries(tags || {})
    .filter(([, t]) => t.category === category)
    .map(([id, t]) => ({ id: Number(id), name: t.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildQuery({ window, program, product, geography }) {
  const p = new URLSearchParams({ window: window || 'since' });
  if (program) p.set('program', String(program));
  if (product) p.set('product', String(product));
  if (geography) p.set('geo', geography);
  return p.toString();
}
